/**
 * Environment validation for the FutureIST Master platform.
 *
 * The server schema is parsed exactly once at module load. If validation fails
 * the process throws a single, human-readable error that lists every missing or
 * invalid variable, so misconfiguration is caught at boot rather than at runtime.
 *
 * Client-safe values (anything that may be inlined into a browser bundle) are
 * validated separately via {@link clientEnv} and may ONLY contain `NEXT_PUBLIC_*`
 * keys. Never read server secrets from `clientEnv`.
 */
import { z } from 'zod';

/** Recognized runtime modes for the platform. */
export const NODE_ENVS = ['development', 'test', 'production'] as const;
export type NodeEnv = (typeof NODE_ENVS)[number];

/** A non-empty string that has been trimmed. */
const nonEmpty = (label: string) =>
  z.string({ required_error: `${label} is required` }).trim().min(1, `${label} must not be empty`);

/** A URL string. Accepts http(s) and common service schemes (redis://, postgres://, s3://). */
const urlLike = (label: string) =>
  nonEmpty(label).refine(
    (value) => /^[a-z][a-z0-9+.-]*:\/\//i.test(value),
    `${label} must be a valid URL (e.g. https://…, postgresql://…, redis://…)`,
  );

/**
 * A datastore connection string. Accepts standard `scheme://…` URLs (postgres,
 * redis, …) AND SQLite-style `file:` paths (e.g. `file:./dev.db`), which have no
 * `//` authority component and would otherwise fail {@link urlLike}.
 */
const dbUrl = (label: string) =>
  nonEmpty(label).refine(
    (value) => /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || /^file:/i.test(value),
    `${label} must be a connection URL (postgresql://…) or a SQLite path (file:./dev.db)`,
  );

/** A strict http/https URL (used for public-facing URLs). */
const httpUrl = (label: string) =>
  nonEmpty(label).refine((value) => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }, `${label} must be a valid http(s) URL`);

/** Coerce a stringified integer with optional bounds. */
const intFromString = (label: string, min = 0, max = 65535) =>
  z.coerce
    .number({ invalid_type_error: `${label} must be a number` })
    .int(`${label} must be an integer`)
    .min(min, `${label} must be >= ${min}`)
    .max(max, `${label} must be <= ${max}`);

/** A duration accepted by jsonwebtoken / ms (e.g. "15m", "7d", "3600"). */
const duration = (label: string) =>
  nonEmpty(label).refine(
    (value) => /^\d+$/.test(value) || /^\d+(ms|s|m|h|d|w|y)$/i.test(value),
    `${label} must be a duration like "15m", "7d", or a number of seconds`,
  );

/** A secret with a minimum length to discourage weak keys in any environment. */
const secret = (label: string, min = 16) =>
  nonEmpty(label).min(min, `${label} must be at least ${min} characters`);

/**
 * An optional string where an empty/blank value (common in a copied `.env`
 * template, e.g. `ANTHROPIC_API_KEY=`) is treated as "unset" rather than an
 * invalid empty string.
 */
const optionalNonEmpty = () =>
  z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
    z.string().trim().min(1).optional(),
  );

/** Like {@link optionalNonEmpty} but falls back to `def` when unset/blank. */
const nonEmptyWithDefault = (def: string) =>
  z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
    z.string().trim().min(1).default(def),
  );

/**
 * An optional, case-insensitive enum where a blank value means "unset"
 * (provider auto-selection). Unknown values fail validation at boot rather
 * than at first use.
 */
const optionalEnum = <const T extends readonly [string, ...string[]]>(values: T) =>
  z.preprocess(
    (value) =>
      typeof value === "string" ? value.trim().toLowerCase() || undefined : value,
    z.enum(values).optional(),
  );

/**
 * A strict optional boolean flag. Unlike `z.coerce.boolean()` (which treats
 * the string "false" as true), this maps "1/true/on/yes" → true and
 * "0/false/off/no" → false; blank/absent stays `undefined` so downstream
 * consumers can apply their own default.
 */
const optionalBoolFlag = () =>
  z.preprocess((value) => {
    if (typeof value === "boolean") return value;
    if (typeof value !== "string") return value;
    const v = value.trim().toLowerCase();
    if (v === "") return undefined;
    if (["1", "true", "on", "yes"].includes(v)) return true;
    if (["0", "false", "off", "no"].includes(v)) return false;
    return value; // let z.boolean() report the invalid value
  }, z.boolean().optional());

/**
 * Environment-aware default: in production, fall back to the futurim.org
 * hostnames instead of localhost — so a prod build/process that's missing a URL
 * env var never silently emits a localhost link. An explicit env var still
 * overrides both; dev/test keep the localhost defaults. (`next build` sets
 * NODE_ENV=production, so client bundles inline the prod values too.)
 */
const PROD_ENV = process.env.NODE_ENV === 'production';
const byEnv = (dev: string, prod: string): string => (PROD_ENV ? prod : dev);

/**
 * The full server-side environment schema. Optional integrations (AI, SMTP, S3)
 * are individually optional, but each is internally consistent: an S3 bucket
 * without credentials, for example, is reported by downstream feature flags.
 */
const serverObjectSchema = z.object({
  // ── Runtime ──────────────────────────────────────────────────────────────
  NODE_ENV: z.enum(NODE_ENVS).default('development'),
  PORT: intFromString('PORT', 1).default(4000),
  WEB_PORT: intFromString('WEB_PORT', 1).default(3000),
  DASHBOARD_PORT: intFromString('DASHBOARD_PORT', 1).default(3001),
  WS_PORT: intFromString('WS_PORT', 1).default(4001),
  HOST: nonEmpty('HOST').default('0.0.0.0'),
  LOG_LEVEL: z
    .enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent'])
    .default('info'),
  /** Service name stamped into every structured log line. */
  SERVICE_NAME: nonEmpty('SERVICE_NAME').default('fim'),

  // ── Runtime mode ─────────────────────────────────────────────────────────
  /**
   * Embedded / zero-dependency mode. When true the platform runs without any
   * external services: SQLite replaces Postgres, an in-process store replaces
   * Redis (cache / queues / pub-sub), the local filesystem replaces S3, and
   * outbound email is written to disk instead of an SMTP relay. Intended for
   * local development without Docker.
   */
  FIM_EMBEDDED: z.coerce.boolean().default(false),

  // ── Datastores ───────────────────────────────────────────────────────────
  DATABASE_URL: dbUrl('DATABASE_URL'),
  /** Direct (non-pooled) connection used by Prisma migrations. Falls back to DATABASE_URL. */
  DIRECT_URL: dbUrl('DIRECT_URL').optional(),
  REDIS_URL: urlLike('REDIS_URL').default('redis://localhost:6379'),

  // ── Auth / JWT ───────────────────────────────────────────────────────────
  JWT_ACCESS_SECRET: secret('JWT_ACCESS_SECRET', 32),
  JWT_REFRESH_SECRET: secret('JWT_REFRESH_SECRET', 32),
  JWT_ACCESS_TTL: duration('JWT_ACCESS_TTL').default('15m'),
  JWT_REFRESH_TTL: duration('JWT_REFRESH_TTL').default('30d'),
  JWT_ISSUER: nonEmpty('JWT_ISSUER').default('futureist-master'),
  JWT_AUDIENCE: nonEmpty('JWT_AUDIENCE').default('fim-platform'),
  COOKIE_SECRET: secret('COOKIE_SECRET', 32).optional(),
  COOKIE_DOMAIN: z.string().trim().optional(),

  // ── Better-Auth / FIM identity (BLUEPRINT §4, Phases 3–9) ────────────────
  /** Canonical origin of the Better-Auth server (the API). Unset → inferred. */
  BETTER_AUTH_URL: httpUrl('BETTER_AUTH_URL').optional(),
  /** Better-Auth signing/encryption secret. REQUIRED + non-default in production. */
  BETTER_AUTH_SECRET: optionalNonEmpty(),
  /**
   * Toggle Better-Auth's built-in per-IP rate limiting. Unset → enabled
   * everywhere except `NODE_ENV=test` (see @fim/auth `createFimAuth`).
   */
  FIM_AUTH_RATE_LIMIT: optionalBoolFlag(),
  /** Embedded PGlite data dir (relative paths resolve against the repo root). */
  FIM_PGLITE_DIR: nonEmptyWithDefault('.data/pglite'),
  /** Root for on-disk dev sinks (mail, …). Unset → `<repo>/.data`. */
  FIM_DATA_DIR: optionalNonEmpty(),
  /** Dev-only seeded studio owner (ignored in production). */
  FIM_OWNER_EMAIL: z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().email('FIM_OWNER_EMAIL must be a valid email address').optional(),
  ),
  FIM_OWNER_PASSWORD: z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().min(8, 'FIM_OWNER_PASSWORD must be at least 8 characters').optional(),
  ),

  // ── Admin panel (FIM dashboard / admin.futurim.org) ──────────────────────
  /** Username of the first admin, seeded on boot when the admin table is empty. */
  ADMIN_USERNAME: nonEmptyWithDefault('admin'),
  /** Password for the seeded admin (bcrypt-hashed on seed). Change in production. */
  ADMIN_PASSWORD: z.string().min(8, 'ADMIN_PASSWORD must be at least 8 characters').default('changeme-admin'),
  /** HMAC secret signing admin-panel session tokens. REQUIRED + non-default in production. */
  ADMIN_SESSION_SECRET: nonEmptyWithDefault('dev-only-admin-session-secret-change-me-32chars-minimum'),

  // ── Payments (BLUEPRINT §5.7, Phase 6) ───────────────────────────────────
  /** Explicit provider override; unset → auto-select (stripe iff key present). */
  FIM_PAYMENTS_PROVIDER: optionalEnum(['stripe', 'dev']),
  STRIPE_SECRET_KEY: optionalNonEmpty(),
  STRIPE_WEBHOOK_SECRET: optionalNonEmpty(),
  /** HMAC secret for the dev provider's simulated stripe-signature header. */
  FIM_DEV_WEBHOOK_SECRET: nonEmptyWithDefault('dev-only-webhook-secret-change-me'),

  // ── Portal files (BLUEPRINT §5.8, Phase 8) ───────────────────────────────
  /** Explicit storage override; unset → auto-select (r2 iff all creds present). */
  FIM_FILES_PROVIDER: optionalEnum(['r2', 'dev']),
  /** Local-disk root for the dev provider (relative paths resolve to repo root). */
  FIM_FILES_DIR: nonEmptyWithDefault('.data/uploads/portal'),
  /** HMAC secret signing short-lived dev upload/download URLs. */
  FIM_DEV_FILES_SECRET: nonEmptyWithDefault('dev-only-files-secret-change-me'),
  R2_ACCOUNT_ID: optionalNonEmpty(),
  R2_ACCESS_KEY_ID: optionalNonEmpty(),
  R2_SECRET_ACCESS_KEY: optionalNonEmpty(),
  R2_BUCKET: optionalNonEmpty(),

  // ── OAuth / social sign-in ───────────────────────────────────────────────
  // All optional: a provider is "configured" only when BOTH its id and secret
  // are present. Register apps at:
  //   GitHub  → https://github.com/settings/developers
  //   Google  → https://console.cloud.google.com/apis/credentials
  //   Apple   → https://developer.apple.com/account/resources (Sign in with Apple)
  // Two auth surfaces consume these, each with its OWN callback URL — register
  // whichever you use on the provider (or both):
  //   • Portal (Better-Auth):  ${BETTER_AUTH_URL}/api/auth/callback/<provider>
  //   • Dashboard (legacy):    ${OAUTH_CALLBACK_BASE_URL ?? API_URL}/api/v1/auth/oauth/<provider>/callback
  GITHUB_OAUTH_CLIENT_ID: optionalNonEmpty(),
  GITHUB_OAUTH_CLIENT_SECRET: optionalNonEmpty(),
  GOOGLE_OAUTH_CLIENT_ID: optionalNonEmpty(),
  GOOGLE_OAUTH_CLIENT_SECRET: optionalNonEmpty(),
  // Apple: client id = Services ID; secret = the generated client-secret JWT.
  // The bundle id is only needed for native (iOS) clients.
  APPLE_OAUTH_CLIENT_ID: optionalNonEmpty(),
  APPLE_OAUTH_CLIENT_SECRET: optionalNonEmpty(),
  APPLE_OAUTH_APP_BUNDLE_IDENTIFIER: optionalNonEmpty(),
  /** Origin the providers redirect back to. Defaults to {@link API_URL}. */
  OAUTH_CALLBACK_BASE_URL: httpUrl('OAUTH_CALLBACK_BASE_URL').optional(),

  // ── AI providers ─────────────────────────────────────────────────────────
  AI_PROVIDER: z.enum(['anthropic', 'openai', 'deepseek']).default('anthropic'),
  ANTHROPIC_API_KEY: optionalNonEmpty(),
  ANTHROPIC_MODEL: nonEmpty('ANTHROPIC_MODEL').default('claude-opus-4-8'),
  OPENAI_API_KEY: optionalNonEmpty(),
  OPENAI_MODEL: nonEmpty('OPENAI_MODEL').default('gpt-4o'),
  DEEPSEEK_API_KEY: optionalNonEmpty(),
  // Legacy aliases (deepseek-chat / deepseek-reasoner) are retired 2026-07-24 and
  // removed from @fim/ai-sdk — reject them here with a clear message rather than
  // letting a stale config silently 400 at request time. (Inlined, not imported
  // from @fim/ai-sdk, because @fim/ai-sdk depends on @fim/config — not the reverse.)
  DEEPSEEK_MODEL: nonEmpty('DEEPSEEK_MODEL')
    .refine((m) => m !== 'deepseek-chat' && m !== 'deepseek-reasoner', {
      message:
        "DEEPSEEK_MODEL 'deepseek-chat' / 'deepseek-reasoner' are deprecated and removed; use 'deepseek-v4-flash' or 'deepseek-v4-pro'.",
    })
    .default('deepseek-v4-flash'),
  /** Override the DeepSeek base URL (proxy / gateway / self-hosted). */
  DEEPSEEK_BASE_URL: urlLike('DEEPSEEK_BASE_URL').optional(),
  AI_EMBEDDING_MODEL: nonEmpty('AI_EMBEDDING_MODEL').default('text-embedding-3-large'),

  // ── FutureX context engine (docs/futurex/context-engine-discovery.md §6.1) ─
  // Which per-turn context provider the futurex gateway uses: 'legacy' (the
  // session-digest summary message — today's behavior, byte-identical) or
  // 'graph' (the Context Graph Engine). Pinned into SessionMeta at session
  // open so an in-flight session never flips mid-conversation (prefix-cache
  // discipline). Stays 'legacy' until the engine wins its Phase 8 eval.
  // (Inlined union, not imported from @fim/futurex-context-engine, to keep
  // @fim/config dependency-free — same reasoning as DEEPSEEK_MODEL above.)
  FUTUREX_CONTEXT_ENGINE: z.enum(['graph', 'legacy']).default('legacy'),

  // ── SMTP / Mail ──────────────────────────────────────────────────────────
  SMTP_HOST: z.string().trim().optional(),
  SMTP_PORT: intFromString('SMTP_PORT', 1).default(587),
  SMTP_USER: z.string().trim().optional(),
  SMTP_PASSWORD: z.string().optional(),
  SMTP_SECURE: z.coerce.boolean().default(false),
  MAIL_FROM: z
    .string()
    .trim()
    .email('MAIL_FROM must be a valid email address')
    .default('no-reply@futureist.io'),
  MAIL_FROM_NAME: nonEmpty('MAIL_FROM_NAME').default('FutureIST Master'),

  // ── Object storage (S3 compatible) ───────────────────────────────────────
  S3_REGION: nonEmpty('S3_REGION').default('us-east-1'),
  S3_ENDPOINT: urlLike('S3_ENDPOINT').optional(),
  S3_BUCKET: z.string().trim().optional(),
  S3_ACCESS_KEY_ID: z.string().trim().optional(),
  S3_SECRET_ACCESS_KEY: z.string().optional(),
  S3_FORCE_PATH_STYLE: z.coerce.boolean().default(false),
  S3_PUBLIC_URL: httpUrl('S3_PUBLIC_URL').optional(),

  // ── Public URLs (server-known canonical origins) ─────────────────────────
  APP_URL: httpUrl('APP_URL').default(byEnv('http://localhost:3000', 'https://futurim.org')),
  DASHBOARD_URL: httpUrl('DASHBOARD_URL').default(
    byEnv('http://localhost:3001', 'https://admin.futurim.org'),
  ),
  PORTAL_URL: httpUrl('PORTAL_URL').default(
    byEnv('http://localhost:3002', 'https://portal.futurim.org'),
  ),
  API_URL: httpUrl('API_URL').default(byEnv('http://localhost:4000', 'https://api.futurim.org')),
  WS_URL: urlLike('WS_URL').default(byEnv('ws://localhost:4001', 'wss://api.futurim.org')),

  // ── CORS / security ──────────────────────────────────────────────────────
  CORS_ORIGINS: z
    .string()
    .trim()
    .default(
      byEnv(
        'http://localhost:3000,http://localhost:3001',
        'https://futurim.org,https://www.futurim.org,https://admin.futurim.org,https://portal.futurim.org',
      ),
    )
    .transform((value) =>
      value
        .split(',')
        .map((origin) => origin.trim())
        .filter(Boolean),
    ),
  RATE_LIMIT_MAX: intFromString('RATE_LIMIT_MAX', 1, 1_000_000).default(100),
  RATE_LIMIT_WINDOW: duration('RATE_LIMIT_WINDOW').default('1m'),

  // ── Geo-IP (visitor tracking enrichment) ─────────────────────────────────
  /**
   * URL template for the external IP→geolocation lookup used when CDN geo
   * headers (Cloudflare/Vercel) are absent — `{ip}` is substituted. Defaults
   * to the free, keyless, HTTPS ipwho.is endpoint so a bare VPS behind nginx
   * still resolves visitor countries. Set to an EMPTY string to disable
   * external lookups entirely (header-only geo).
   */
  GEOIP_LOOKUP_URL: z
    .string()
    .trim()
    .default('https://ipwho.is/{ip}')
    .refine(
      (value) => value === '' || (/^https?:\/\//i.test(value) && value.includes('{ip}')),
      'GEOIP_LOOKUP_URL must be an http(s) URL template containing {ip} (or empty to disable)',
    ),

  // ── Feature toggles (raw; parsed in feature-flags.ts) ────────────────────
  FEATURE_AI_ASSISTANT: z.coerce.boolean().optional(),
  FEATURE_REALTIME: z.coerce.boolean().optional(),
  FEATURE_ANALYTICS: z.coerce.boolean().optional(),
  FEATURE_BILLING: z.coerce.boolean().optional(),
  FEATURE_FILE_UPLOADS: z.coerce.boolean().optional(),
  FEATURE_EMAIL_NOTIFICATIONS: z.coerce.boolean().optional(),
  MAINTENANCE_MODE: z.coerce.boolean().optional(),
});

/**
 * Production-only consistency refinements (BLUEPRINT Phase 9 hardening).
 * Development/test keep the permissive optional-with-defaults behavior.
 */
const serverSchema = serverObjectSchema.superRefine((cfg, ctx) => {
  if (cfg.NODE_ENV !== 'production') return;

  // Better-Auth secret: required, non-trivial, and never a dev placeholder.
  if (!cfg.BETTER_AUTH_SECRET) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['BETTER_AUTH_SECRET'],
      message: 'BETTER_AUTH_SECRET is required in production',
    });
  } else if (
    cfg.BETTER_AUTH_SECRET.length < 32 ||
    cfg.BETTER_AUTH_SECRET.includes('change-me') ||
    cfg.BETTER_AUTH_SECRET.startsWith('dev-')
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['BETTER_AUTH_SECRET'],
      message:
        'BETTER_AUTH_SECRET must be a real secret in production (>= 32 chars, not a dev placeholder)',
    });
  }

  // Stripe credentials are mandatory when the provider is explicitly stripe.
  if (cfg.FIM_PAYMENTS_PROVIDER === 'stripe') {
    for (const key of ['STRIPE_SECRET_KEY', 'STRIPE_WEBHOOK_SECRET'] as const) {
      if (!cfg[key]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: `${key} is required in production when FIM_PAYMENTS_PROVIDER=stripe`,
        });
      }
    }
  }

  // R2 credentials are mandatory when the files provider is explicitly r2.
  if (cfg.FIM_FILES_PROVIDER === 'r2') {
    for (const key of [
      'R2_ACCOUNT_ID',
      'R2_ACCESS_KEY_ID',
      'R2_SECRET_ACCESS_KEY',
      'R2_BUCKET',
    ] as const) {
      if (!cfg[key]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: `${key} is required in production when FIM_FILES_PROVIDER=r2`,
        });
      }
    }
  }
});

/** Inferred shape of the validated server environment. */
export type ServerEnv = z.infer<typeof serverObjectSchema>;

/** Public client schema. ONLY `NEXT_PUBLIC_*` keys are permitted here. */
const clientSchema = z.object({
  NEXT_PUBLIC_APP_NAME: nonEmpty('NEXT_PUBLIC_APP_NAME').default('FutureIST Master'),
  NEXT_PUBLIC_APP_URL: httpUrl('NEXT_PUBLIC_APP_URL').default(
    byEnv('http://localhost:3000', 'https://futurim.org'),
  ),
  // Includes the versioned API prefix: the dashboard's api-client and AuthProvider
  // address routes WITHOUT re-stating `/api/v1` (e.g. `/auth/login`, `/projects`),
  // so the base must carry it. Backend mounts every module under `/api/v1`.
  NEXT_PUBLIC_API_URL: httpUrl('NEXT_PUBLIC_API_URL').default(
    byEnv('http://localhost:4000/api/v1', 'https://api.futurim.org/api/v1'),
  ),
  // Browser-side origin of the Better-Auth server (no /api/v1 suffix — the
  // auth client appends /api/auth itself).
  NEXT_PUBLIC_AUTH_URL: httpUrl('NEXT_PUBLIC_AUTH_URL').default(
    byEnv('http://localhost:4000', 'https://api.futurim.org'),
  ),
  // The portal (apps/portal) origin, used by site CTAs and cross-app links.
  NEXT_PUBLIC_PORTAL_URL: httpUrl('NEXT_PUBLIC_PORTAL_URL').default(
    byEnv('http://localhost:3002', 'https://portal.futurim.org'),
  ),
  NEXT_PUBLIC_WS_URL: urlLike('NEXT_PUBLIC_WS_URL').default(
    byEnv('ws://localhost:4001', 'wss://api.futurim.org'),
  ),
  NEXT_PUBLIC_ENV: z.enum(NODE_ENVS).default('development'),
  NEXT_PUBLIC_ANALYTICS_WRITE_KEY: z.string().trim().optional(),
  NEXT_PUBLIC_SENTRY_DSN: z.string().trim().optional(),
});

/** Inferred shape of the validated client environment. */
export type ClientEnv = z.infer<typeof clientSchema>;

/**
 * Format a {@link z.ZodError} into a single multi-line message listing each
 * offending variable so the failure is actionable at a glance.
 */
function describeEnvError(error: z.ZodError, scope: 'server' | 'client'): string {
  const lines = error.issues.map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join('.') : '(root)';
    return `  • ${path}: ${issue.message}`;
  });
  return [
    `Invalid ${scope} environment configuration. ${error.issues.length} problem(s) found:`,
    ...lines,
    '',
    `Fix the variables above in your .env file (or deployment secrets) and restart.`,
  ].join('\n');
}

/**
 * Parse the server environment from a source object (defaults to `process.env`).
 * Throws an {@link Error} with a clear, aggregated message on failure.
 */
export function parseServerEnv(source: NodeJS.ProcessEnv = process.env): ServerEnv {
  const parsed = serverSchema.safeParse(source);
  if (!parsed.success) {
    throw new Error(describeEnvError(parsed.error, 'server'));
  }
  // DIRECT_URL conventionally mirrors DATABASE_URL when not explicitly set.
  return {
    ...parsed.data,
    DIRECT_URL: parsed.data.DIRECT_URL ?? parsed.data.DATABASE_URL,
  };
}

/**
 * Parse the client environment from a source object (defaults to `process.env`).
 * Safe to call in the browser when bundlers inline `NEXT_PUBLIC_*` values.
 */
export function parseClientEnv(source: NodeJS.ProcessEnv = process.env): ClientEnv {
  const parsed = clientSchema.safeParse(source);
  if (!parsed.success) {
    throw new Error(describeEnvError(parsed.error, 'client'));
  }
  return parsed.data;
}

/**
 * On the server, validation is memoized and deferred to the FIRST property
 * access on {@link env}: services that read `env.*` at boot (the API, workers)
 * still fail fast with the aggregated message, while modules that only need
 * {@link clientEnv} or {@link siteConfig} (e.g. the marketing site's
 * robots/sitemap during `next build`, where NODE_ENV=production but no server
 * secrets exist) can be imported without triggering server-secret validation.
 * In the browser we skip the server parse entirely (its secrets are never
 * present) and build a stub so importing `env` from client code does not crash
 * — though client code should use {@link clientEnv} instead.
 */
const isServer = typeof (globalThis as { window?: unknown }).window === 'undefined';

/** Memoized result of the one-time server parse (see {@link env}). */
let serverEnvCache: ServerEnv | undefined;

/** Parse-and-memoize the server environment (throws on invalid config). */
function resolveServerEnv(): ServerEnv {
  // NOT frozen: the proxy below rejects writes itself, and reporting frozen
  // (non-configurable) descriptors through a proxy whose target is `{}` would
  // violate proxy invariants.
  serverEnvCache ??= parseServerEnv();
  return serverEnvCache;
}

/**
 * The validated, typed, immutable server environment. Parsed once, on first
 * property access (lazy via Proxy — see the note above).
 */
export const env: ServerEnv = isServer
  ? (new Proxy({} as ServerEnv, {
      get: (_target, prop) => Reflect.get(resolveServerEnv(), prop),
      has: (_target, prop) => Reflect.has(resolveServerEnv(), prop),
      ownKeys: () => Reflect.ownKeys(resolveServerEnv()),
      getOwnPropertyDescriptor: (_target, prop) => {
        const desc = Reflect.getOwnPropertyDescriptor(resolveServerEnv(), prop);
        // Report configurable so the (extensible, empty) proxy target does not
        // violate invariants; immutability is enforced by the traps below.
        return desc ? { ...desc, configurable: true } : undefined;
      },
      set: () => false,
      defineProperty: () => false,
      deleteProperty: () => false,
    }) as ServerEnv)
  : (Object.freeze(
      // On the client, only NEXT_PUBLIC_* exist; coerce a best-effort view so a
      // stray server-side import in a shared module does not throw. Access to a
      // real secret will be `undefined`, which downstream guards already handle.
      serverObjectSchema.partial().parse(
        typeof process !== 'undefined' ? (process.env ?? {}) : {},
      ),
    ) as ServerEnv);

/** The validated, typed, immutable client environment. Parsed once. */
export const clientEnv: ClientEnv = Object.freeze(
  parseClientEnv(typeof process !== 'undefined' ? process.env : ({} as NodeJS.ProcessEnv)),
);

/**
 * Raw NODE_ENV view, mirroring the schema's enum-with-default. Computed from
 * `process.env` directly so the convenience predicates below do not force the
 * full (lazy) server-env parse at import time.
 */
const rawNodeEnv: NodeEnv = (() => {
  const raw = typeof process !== 'undefined' ? process.env.NODE_ENV : undefined;
  return raw !== undefined && (NODE_ENVS as readonly string[]).includes(raw)
    ? (raw as NodeEnv)
    : 'development';
})();

/** Convenience predicates derived from NODE_ENV. */
export const isProduction = rawNodeEnv === 'production';
export const isDevelopment = rawNodeEnv === 'development';
export const isTest = rawNodeEnv === 'test';

/**
 * Whether the platform is running in embedded / zero-dependency mode (SQLite +
 * in-process Redis substitutes + local filesystem storage + on-disk mail).
 * Mirrors the schema's `z.coerce.boolean()` on FIM_EMBEDDED (any non-empty
 * string → true) without forcing the lazy server parse.
 */
export const isEmbedded =
  isServer && typeof process !== 'undefined' && Boolean(process.env.FIM_EMBEDDED);

/** Whether the runtime currently executes on the server. */
export const IS_SERVER = isServer;
